import { useEffect, useState } from "react";
import api from "../../api/Client";
import { SecureStorage } from "../../storage/SecureStorage";
import "../../styles/wallet.css";

/* ========= Utilidades de normalización ========= */
/**
 * - trim
 * - colapsa espacios múltiples
 * - quita signos/emoji innecesarios
 * - pasa a minúsculas
 * - elimina diacríticos (tildes)
 */
function normalize(text: string) {
  return (text ?? "")
    .toString()
    .trim()
    .replace(/\s+/g, " ")
    // quita emojis y símbolos comunes que rompen el matching (mantén letras/números/espacios/.-_/#+)
    .replace(/[^\p{L}\p{N}\s.\-_/#+]/gu, "")
    .toLowerCase()
    // elimina diacríticos
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/* ========= Diccionario de traducciones ========= */
/** 
 * Define aquí las variantes tal como podrían venir del backend.
 * No te preocupes por mayúsculas/espacios: se normalizan debajo.
 */
const RAW_TRANSLATIONS: Record<string, string> = {
  "Add amount": "Ingreso de saldo",
  "Add mount": "Ingreso de saldo",
  "Add balance": "Ingreso de saldo",
  "Top up": "Ingreso de saldo",

  "Payment": "Pago",
  "Charge": "Pago",

  "Refund": "Reembolso",
  "Withdrawal": "Retirada",
};

/** Crea un mapa con las claves ya normalizadas para matching exacto. */
const TRANSLATIONS: Record<string, string> = Object.fromEntries(
  Object.entries(RAW_TRANSLATIONS).map(([k, v]) => [normalize(k), v])
);

/**
 * Traduce de forma robusta:
 * 1) Match exacto (con texto normalizado).
 * 2) Match por prefijo: "payment - card ****" => "Pago"
 * 3) Match por "contiene": "refund #123" => "Reembolso"
 * 4) Fallback: devuelve el texto original.
 */
function translate(text: string | null | undefined) {
  const original = text ?? "";
  const norm = normalize(original);

  if (!norm) return original;

  // 1) Exacto
  if (TRANSLATIONS[norm]) return TRANSLATIONS[norm];

  // 2) Prefijos comunes
  for (const [keyNorm, value] of Object.entries(TRANSLATIONS)) {
    if (norm.startsWith(keyNorm + " ") || norm === keyNorm) {
      return value;
    }
  }

  // 3) Contiene (para sufijos como IDs, métodos de pago, etc.)
  for (const [keyNorm, value] of Object.entries(TRANSLATIONS)) {
    if (norm.includes(keyNorm)) {
      return value;
    }
  }

  // 4) Fallback
  return original;
}

/* ========= Tipos ========= */
interface Transaction {
  date: string;
  amount: number;
  description: string | null; // tolerante a null/undefined
}

/* ========= Componente ========= */
export default function ElectronicWalletPage() {
  const [balance, setBalance] = useState<number>(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const user = await SecureStorage.getUser();
        if (!user?.id) return;

        const balRes = await api.get<{ balance: number }>(
          "/api/mobile/user/e-wallet-balance",
          { params: { user_id: user.id } }
        );
        setBalance(balRes.data.balance ?? 0);

        const txRes = await api.get<{ transactions: Transaction[] }>(
          "/api/mobile/user/transactions",
          { params: { user_id: user.id } }
        );
        setTransactions(txRes.data.transactions ?? []);
      } catch (err) {
        console.error("❌ Error cargando e-wallet:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="wallet-page">
      <div className="wallet-card">
        <h3>💳 Saldo actual: {balance.toFixed(2)} €</h3>

        <button
          className="wallet-toggle"
          onClick={() => setShowDetails(!showDetails)}
        >
          {showDetails ? "🔼 Ocultar detalles" : "▶️ Ver detalles"}
        </button>

        {showDetails && (
          <div className="wallet-list">
            {transactions.length === 0 && <p>No hay transacciones</p>}
            {transactions.map((tx, idx) => (
              <div key={idx} className="wallet-item">
                <p>📅 {tx.date?.substring(0, 10)}</p>
                <p className={tx.amount >= 0 ? "positive" : "negative"}>
                  💰 {tx.amount > 0 ? "+" : ""}{tx.amount} €
                </p>
                <p>📝 {translate(tx.description)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      {loading && <p>Cargando...</p>}
    </div>
  );
}
